import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Injector,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatDividerModule } from '@angular/material/divider';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatNativeDateModule,
} from '@angular/material/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { TokenToDivPipe } from '../../../helpers/token-to-div.pipe';
import { WidgetBase } from '../../../models/widget-base';
import { parseLocalDateString } from '../../../helpers/date-utils';
import { BehaviorSubject, finalize, forkJoin, Observable, of, tap } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
} from 'rxjs/operators';
import { environment } from '@nrwl/environment';
import { TextareaAutosizeDirective } from '../shared/textarea-autosize-directive';
import { PasteStripDirective } from '../shared/paste-strip-directive';
import { ShotMetadata } from '@nrwl/rx-models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UOM_FALLBACK_OPTIONS, UomOption } from './fb-emr-shots.types';
import { WidgetApiService } from '../../../rx-form-presenter/services/widget-api.service';
import { PneInjectable, PneInjectionSite } from '../../../rx-form-presenter/types/pne.types';
import { RxIconModule } from '@nrwl/rx-icon';
import { isArray, isEmpty } from 'lodash';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { DATE_FORMATS, SHOTS_ERROR_TEXTS } from './fb-emr-shots.constants';
import { FbEmrShotsWidget } from '../../../models/form-widget-emr-shots.model';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DateInputGuardDirective, DecimalDisplayDirective } from '../../../helpers/directives';
import { EmrReadonlyNotice } from '../../../helpers/components/emr-readonly-notice';
import { ScribeSuggestionIndicatorComponent } from '../shared/scribe-suggestion-indicator/scribe-suggestion-indicator.component';
import { AmendmentFieldIndicatorComponent } from '../shared/amendment-field-indicator/amendment-field-indicator.component';
import {
  ScribeSuggestionMarker,
  ScribeSuggestionMarkerTarget,
} from '../../scribe-suggestion-marker.model';
import { copyScribeSuggestionMarker } from '../../scribe-suggestion-marker.util';

type SelectFilterOption<T> = {
  items: BehaviorSubject<T[]>;
  isLoaded: BehaviorSubject<boolean>;
  isLoading?: BehaviorSubject<boolean>;
};

type ShotField = {
  name: string;
  label: string;
  type: string;
  placeholder?: string;
  fullRow?: boolean;
  fullWidth?: boolean;
  optionLabelKey?: string;
  optionValueKey?: string;
  hasConflict?: boolean;
};

type ShotGroup = FormGroup<{
  id: FormControl<string | null>;
  date: FormControl<string | null>;
  expire: FormControl<string | null>;
  compound: FormControl<string | null>;
  compound_name: FormControl<string | null>;
  amount: FormControl<number | null>;
  uom: FormControl<string | null>;
  uom_name: FormControl<string | null>;
  site: FormControl<string | null>;
  site_name: FormControl<string | null>;
  employee_id: FormControl<string | null>;
  employee_name: FormControl<string | null>;
  lot_number: FormControl<string | null>;
  notes: FormControl<string | null>;
  isManuallyAdded: FormControl<boolean>;
}>;

type ShotFormData = ShotMetadata & {
  compound_name?: string | null;
  uom_name?: string | null;
  site_name?: string | null;
  employee_name?: string | null;
  isScribeSuggested?: ScribeSuggestionMarker;
};

@Component({
  selector: 'fb-emr-shots',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDividerModule,
    MatDatepickerModule,
    MatNativeDateModule,
    NgSelectModule,
    RxIconModule,
    TokenToDivPipe,
    TextareaAutosizeDirective,
    PasteStripDirective,
    ReactiveFormsModule,
    DateInputGuardDirective,
    MatProgressSpinner,
    DecimalDisplayDirective,
    EmrReadonlyNotice,
    ScribeSuggestionIndicatorComponent,
    AmendmentFieldIndicatorComponent,
  ],
  providers: [
    DecimalPipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS },
  ],
  templateUrl: './fb-emr-shots.component.html',
  styleUrl: './fb-emr-shots.component.scss',
})
export class FbEmrShotsComponent extends WidgetBase implements OnInit {
  @Input() public isTemplatePrint = false;

  @Input()
  public override set isEmrReadonly(value: boolean) {
    super.isEmrReadonly = value;
    this.applyDisabledState();
  }
  public override get isEmrReadonly() {
    return super.isEmrReadonly;
  }

  @Input()
  public override set payload(value) {
    this._payload = value;
  }
  public override get payload() {
    return this._payload;
  }

  @Input()
  public override set patientId(value) {
    this._patientId = value;
    if (value && !this.isPreview && !this.isPrinting) {
      if (this._lastLoadedPatientId !== value) {
        this._lastLoadedPatientId = value;
      }
    }
  }
  public override get patientId() {
    return this._patientId;
  }

  @Input()
  public override set patient(value) {
    this._patient = value;
    if (value?.remote_id && !this.patientId && !this.isPreview && !this.isPrinting) {
      if (this._lastLoadedPatientId !== value.remote_id) {
        this._lastLoadedPatientId = value.remote_id;
      }
    }
  }
  public override get patient() {
    return this._patient;
  }

  @Input()
  public override set entity(value) {
    this._entity = value;
    this.formTemplateId = this._entity?.template?.id ?? this._entity?.id;
  }
  public override get entity() {
    return this._entity;
  }

  public formTemplateId: string;
  public employees$: BehaviorSubject<{ text: string; value: string }[]> = new BehaviorSubject<
    { text: string; value: string }[]
  >([]);
  public isLoadingEmployees = signal(false);
  public hasLoadedEmployees = signal(false);
  public selectFilterOptionsMap: {
    compound: SelectFilterOption<PneInjectable>;
    site: SelectFilterOption<PneInjectionSite>;
    uom: SelectFilterOption<UomOption>;
  } = {
    compound: {
      items: new BehaviorSubject<PneInjectable[]>([]),
      isLoaded: new BehaviorSubject<boolean>(false),
      isLoading: new BehaviorSubject<boolean>(false),
    },
    site: {
      items: new BehaviorSubject<PneInjectionSite[]>([]),
      isLoaded: new BehaviorSubject<boolean>(false),
      isLoading: new BehaviorSubject<boolean>(false),
    },
    uom: {
      items: new BehaviorSubject<UomOption[]>(UOM_FALLBACK_OPTIONS),
      isLoaded: new BehaviorSubject<boolean>(true),
      isLoading: new BehaviorSubject<boolean>(false),
    },
  };
  public readonly errorTexts = SHOTS_ERROR_TEXTS;
  public readonly decimalFormat = '1.3-3';
  public readonly decimalLocale = 'en-US';
  public isWidgetHidden = false;

  private _lastLoadedPatientId: number | null = null;
  private searchTerm$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private currentPage = 1;
  private pageSize = 30;
  private hasMoreEmployees = true;
  private allEmployees: { text: string; value: string }[] = [];
  private compoundCurrentPage = 1;
  private hasMoreCompounds = true;
  private suppressPayloadUpdate = false;
  private readonly markerTargetByGroup = new WeakMap<ShotGroup, ScribeSuggestionMarkerTarget>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly http = inject(HttpClient);
  private readonly fb = inject(FormBuilder);
  private readonly widgetApiService = inject(WidgetApiService);
  private readonly decimalPipe = inject(DecimalPipe);

  public readonly form = this.fb.group({
    items: this.fb.array<ShotGroup>([]),
  });
  public isFormLoaded = new BehaviorSubject<boolean>(false);

  constructor(private injector: Injector) {
    super(injector);
  }

  public get shotsFormArray(): FormArray<ShotGroup> {
    return this.form?.controls?.items;
  }

  public get shotFields(): ShotField[] {
    return (this.payload?.properties?.items?.fields ?? []) as ShotField[];
  }

  public get props(): unknown {
    return this.payload?.properties;
  }

  public get labelText(): string | null {
    return this.propValue<string>('text');
  }

  public get contentText(): string | null {
    return this.propValue<string>('content');
  }

  public get hintText(): string | null {
    return this.propValue<string>('hint');
  }

  public get isRequired(): boolean {
    return !!this.propValue<boolean>('required');
  }

  public get hideLabel(): boolean {
    return !!this.propValue<boolean>('hideLabel');
  }

  public get showLabel(): boolean {
    return !!this.labelText && !this.hideLabel;
  }

  public get shouldInitializeDate(): boolean {
    return !this.isPreview && !this.isPrinting && !this.isEmrReadonly;
  }

  /**
   * True while a patient's own plan is being customized. The widget then draws one default shot
   * and never reads or writes the saved shots, so they are stored back unchanged.
   */
  public get isInstanceDesign(): boolean {
    return !!this.isDesign && !!this.entity;
  }

  public ngOnInit(): void {
    this.initializeFormValueChanges();
    this.applyDisabledState();
    this.initializeEmployeeSearch();
    this.initializeForm();
    this.loadShotsData();
  }

  public customSearchFn = () => true;

  public placeholderFor = (field: ShotField) =>
    this.isPrinting || this.isPreview || this.isEmrReadonly ? '' : (field?.placeholder ?? '');

  public controlId(
    fieldName: string | number | symbol,
    shotIndex: number,
    fieldIndex: number,
  ): string {
    return `shot-${String(fieldName)}-${shotIndex}-${fieldIndex}`;
  }

  public getMetadata(): Record<string, unknown> {
    if (this.isWidgetHidden) {
      return {};
    }

    if (this.payload && this.payload.properties) {
      const widgetData = { ...this.payload };
      widgetData.properties = { ...widgetData.properties };

      const widget = new FbEmrShotsWidget(widgetData);
      return widget.getMetadata();
    }

    return { shots: [] };
  }

  public onSearch(searchTerm: { term: string }): void {
    this.searchTerm$.next(searchTerm.term);
  }

  public onScrollToEnd(): void {
    if (!this.hasMoreEmployees || this.isLoadingEmployees()) return;
    this.currentPage++;
    this.loadEmployees(this.searchTerm$.value);
  }

  public onSelectScrollToEnd(fieldName: string): void {
    if (fieldName !== 'compound') {
      return;
    }

    const state = this.selectFilterOptionsMap.compound;

    if (!this.hasMoreCompounds || state.isLoading?.value) {
      return;
    }

    const nextPage = this.compoundCurrentPage + 1;

    this.loadCompoundData(nextPage).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  public addShot(): void {
    if (this.isInstanceDesign) {
      return;
    }
    if (this.isWidgetHidden) {
      this.showWidget();
    }
    this.shotsFormArray.push(this.createShotGroup(true));
    this.shotsFormArray.markAsDirty();
    this.cd.markForCheck();
  }

  public applySuggestionSet(patch: Partial<ShotFormData>): void {
    if (this.isInstanceDesign) {
      return;
    }
    this.showWidget();

    const targetGroup = this.ensureSuggestionTargetGroup();
    if (!targetGroup) {
      return;
    }

    const patchValue: Record<string, unknown> = {};

    if (patch.date !== undefined) {
      patchValue['date'] = this.normalizeDate(patch.date) ?? null;
    }
    if (patch.expire !== undefined) {
      patchValue['expire'] = this.normalizeDate(patch.expire) ?? null;
    }
    if (patch.amount !== undefined) {
      patchValue['amount'] = patch.amount;
    }
    if (patch.uom !== undefined) {
      const selection = this.resolveReviewSelection('uom', patch.uom);
      patchValue['uom'] = selection.id;
      patchValue['uom_name'] = selection.label;
    } else if (patch.uom_name !== undefined) {
      const selection = this.resolveReviewSelection('uom', patch.uom_name);
      patchValue['uom'] = selection.id;
      patchValue['uom_name'] = selection.label;
    }
    if (patch.lot_number !== undefined) {
      patchValue['lot_number'] = patch.lot_number;
    }
    if (patch.notes !== undefined) {
      patchValue['notes'] = patch.notes;
    }

    if (patch.compound !== undefined) {
      const selection = this.resolveReviewSelection('compound', patch.compound);
      patchValue['compound'] = selection.id;
      patchValue['compound_name'] = selection.label;
    }
    if (patch.site !== undefined) {
      const selection = this.resolveReviewSelection('site', patch.site);
      patchValue['site'] = selection.id;
      patchValue['site_name'] = selection.label;
    }
    if (patch.employee_id !== undefined) {
      const selection = this.resolveReviewSelection('employee_id', patch.employee_id);
      patchValue['employee_id'] = selection.id;
      patchValue['employee_name'] = selection.label;
    }

    targetGroup.patchValue(patchValue, { emitEvent: false });
    targetGroup.markAsDirty();
    this.shotsFormArray.markAsDirty();
    this.updatePayloadFromForm();
    this.cd.markForCheck();
  }

  public applyFieldSuggestion(fieldName: string, value: unknown): void {
    this.applySuggestionSet({ [fieldName]: value } as Partial<ShotFormData>);
  }

  public getShotsCompoundOptionsSnapshot(): Array<{ id: string; label: string }> {
    return this.selectFilterOptionsMap.compound.items.value
      .map((item) => ({
        id: `${item?.InjectableId ?? ''}`.trim(),
        label: `${item?.InjectableName ?? ''}`.trim(),
      }))
      .filter((item) => !!item.id && !!item.label);
  }

  public getShotsSiteOptionsSnapshot(): Array<{ id: string; label: string }> {
    return this.selectFilterOptionsMap.site.items.value
      .map((item) => ({
        id: `${item?.InjectionSiteId ?? ''}`.trim(),
        label: `${item?.InjectionSiteName ?? ''}`.trim(),
      }))
      .filter((item) => !!item.id && !!item.label);
  }

  public getShotsUomOptionsSnapshot(): Array<{ id: string; label: string }> {
    return this.selectFilterOptionsMap.uom.items.value
      .map((item) => ({
        id: `${item?.UomId ?? ''}`.trim(),
        label: `${item?.UomName ?? ''}`.trim(),
      }))
      .filter((item) => !!item.id && !!item.label);
  }

  public getShotsEmployeeOptionsSnapshot(): Array<{ id: string; label: string }> {
    return this.employees$.value
      .map((item) => ({
        id: `${item?.value ?? ''}`.trim(),
        label: `${item?.text ?? ''}`.trim(),
      }))
      .filter((item) => !!item.id && !!item.label);
  }

  public updatePayload(): void {
    this.updatePayloadFromForm();
  }

  public getShotMarkerTarget(index: number): ScribeSuggestionMarkerTarget | null {
    const group = this.shotsFormArray.at(index);
    return group ? (this.markerTargetByGroup.get(group) ?? null) : null;
  }

  public clearShotMarker(index: number, fieldKey: string): void {
    this.clearScribeSuggestionMarker(this.getShotMarkerTarget(index), fieldKey);
  }

  private ensureSuggestionTargetGroup(): ShotGroup | null {
    if (this.shotsFormArray.length === 0) {
      const nextGroup = this.createShotGroup(true);
      this.shotsFormArray.push(nextGroup);
      return nextGroup;
    }

    const existing = this.shotsFormArray.controls.find((group) => !this.isShotMeaningful(group));
    if (existing) {
      return existing;
    }

    const nextGroup = this.createShotGroup(true);
    this.shotsFormArray.push(nextGroup);
    return nextGroup;
  }

  public removeShot(index: number): void {
    if (this.isInstanceDesign) {
      return;
    }
    if (this.shotsFormArray.length <= 1) {
      this.hideWidget();
      return;
    }

    this.shotsFormArray.removeAt(index);
    this.shotsFormArray.markAsDirty();
    this.cd.markForCheck();
  }

  public isFieldRequired(shotGroup: AbstractControl, fieldName: string): boolean {
    return shotGroup.get(fieldName)?.hasValidator(Validators.required) ?? false;
  }

  public override isValid(): boolean {
    if (this.isWidgetHidden) {
      return true;
    }

    return this.form ? this.form.valid : true;
  }

  public getFieldErrorByControl(control: AbstractControl, label: string): string | null {
    if (!this.isSubmitted || !control) {
      return null;
    }

    if (control.hasError('matDatepickerParse')) {
      return this.errorTexts.invalidDateFormat;
    }

    if (control.hasError('min')) {
      return `${label} ${this.errorTexts.min} 0.`;
    }

    if (control.hasError('max')) {
      const max = this.decimalPipe.transform(
        control.getError('max').max,
        this.decimalFormat,
        this.decimalLocale,
      );

      return `${label} ${this.errorTexts.max} ${max}.`;
    }

    if (control.hasError('required')) {
      return `${label} ${this.errorTexts.required}`;
    }

    return null;
  }

  public getVisibleShotEntries(): Array<{ index: number; group: ShotGroup }> {
    const entries = this.shotsFormArray.controls.map((group, index) => ({ index, group }));
    if (!this.isPrinting || this.isTemplatePrint) {
      return entries;
    }

    return entries.filter((entry) => this.isShotMeaningful(entry.group));
  }

  public getPrintDisplayValue(shotGroup: AbstractControl, field: ShotField): string {
    const controlValue = this.normalizeString(shotGroup.get(field.name)?.value);
    if (field.type === 'date') {
      return this.normalizeDate(controlValue ?? '') ?? '';
    }

    if (field.type === 'number') {
      if (!controlValue) {
        return '';
      }

      return (
        this.decimalPipe.transform(controlValue, this.decimalFormat, this.decimalLocale) ??
        controlValue
      );
    }

    const hasSelectOrAutocomplete = field.type === 'select' || field.type === 'autocomplete';
    if (!hasSelectOrAutocomplete) {
      return controlValue ?? '';
    }

    const namesByField: Record<string, string> = {
      compound: 'compound_name',
      uom: 'uom_name',
      site: 'site_name',
      employee_id: 'employee_name',
    };

    const nameControl = namesByField[field.name];
    const savedName = nameControl ? this.normalizeString(shotGroup.get(nameControl)?.value) : null;
    const resolvedName = this.resolveLabelByLookup(field.name, controlValue);
    return resolvedName ?? savedName ?? controlValue ?? '';
  }

  private getShotMetadataFromPayload(): ShotFormData[] {
    const metadata = this.entity?.metadata?.shots;
    return Array.isArray(metadata) ? metadata : [];
  }

  private getShotsFromPayloadValue(): ShotFormData[] {
    const items = this.payload?.properties?.items?.value;
    return Array.isArray(items) ? items : [];
  }

  private initializeFormValueChanges(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.updatePayloadFromForm();
    });
  }

  private propValue<T = unknown>(key: string): T | null {
    return this.props?.[key]?.value ?? null;
  }

  private initializeEmployeeSearch(): void {
    if (this.isPrinting || this.isEmrReadonly) {
      return;
    }

    if (!this.patient) {
      return;
    }

    const hasEmployeeInShots = [
      ...this.getShotsFromPayloadValue(),
      ...this.getShotMetadataFromPayload(),
    ].some((shot) => !!shot.employee_id);

    if ((this.isPreview || this.isPrinting) && !hasEmployeeInShots) {
      return;
    }

    this.searchTerm$
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(300), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.currentPage = 1;
        this.hasMoreEmployees = true;
        this.loadEmployees(searchTerm);
      });
  }

  private loadEmployees(searchTerm = ''): void {
    if (this.isPrinting || this.isEmrReadonly) {
      return;
    }

    if (this.isLoadingEmployees()) {
      return;
    }

    this.isLoadingEmployees.set(true);

    const params = new URLSearchParams();
    params.append('Page', this.currentPage.toString());
    params.append('Rows', this.pageSize.toString());
    params.append('IsActive', 'true');
    if (searchTerm) {
      params.append('FullName', searchTerm);
    }

    const url = `${environment.baseAPIUrlMicro}egress-proxy/pne/employees?${params.toString()}`;

    this.http
      .get<any>(url)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((response) => {
          const data = response?.Data || response || [];
          return data.map((emp: any) => ({
            text: `${emp.FirstName || ''} ${emp.LastName || ''}`.trim(),
            value: emp.Id || emp.EmployeeId,
          }));
        }),
        catchError(() => of([])),
      )
      .subscribe((employees) => {
        if (this.currentPage === 1) {
          this.allEmployees = this.mergeEmployees(employees, this.getSelectedEmployeeOptions());
        } else {
          this.allEmployees = this.mergeEmployees(this.allEmployees, employees);
        }

        this.employees$.next(this.allEmployees);
        this.hasMoreEmployees = employees.length >= this.pageSize;
        this.isLoadingEmployees.set(false);
        this.hasLoadedEmployees.set(true);
        this.updatePayloadFromForm();
        this.cd.markForCheck();
      });
  }

  private initializeForm(): void {
    if (this.isInstanceDesign) {
      // Draw the default look, even when the saved widget is hidden or holds several shots.
      this.isWidgetHidden = false;
      this.initializeFormFromPayload([]);
      this.applyDisabledState();
      this.isFormLoaded.next(true);
      return;
    }

    const isHidden = !!this.payload?.properties?.items?.hidden;
    if (isHidden) {
      this.isWidgetHidden = true;
      this.shotsFormArray.clear();
      this.isFormLoaded.next(true);
      return;
    }

    this.loadFormData()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (shots) => {
          this.initializeFormFromPayload(shots);
          this.addSelectedCompoundOptions(shots);
          this.addSelectedEmployeeOptions();
          this.applyDisabledState();
          this.isFormLoaded.next(true);
        },
        error: () => {
          this.initializeFormFromPayload([]);
          this.applyDisabledState();
          this.isFormLoaded.next(true);
        },
      });
  }

  private loadShotsData(): void {
    if (this.isInstanceDesign || this.isPrinting || this.isEmrReadonly) {
      this.ready.next(true);
      return;
    }

    if (this.patient) {
      this.ready.next(false);
      forkJoin({
        compound: this.loadCompoundData(),
        formInitialized: this.isFormLoaded.pipe(
          filter((isLoaded) => isLoaded),
          take(1),
        ),
        site: this.loadSitesData(),
      })
        .pipe(
          switchMap(() => this.resolveSelectedCompoundNames()),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe({
          next: () => {
            this.updatePayloadFromForm();
            this.ready.next(true);
          },
          error: () => {
            this.ready.next(true);
          },
        });
    } else {
      this.ready.next(true);
    }
  }

  private loadCompoundData(page = 1): Observable<PneInjectable[]> {
    if (this.isPrinting || this.isEmrReadonly) {
      return of([]);
    }

    const hasCompoundInShots = [
      ...this.getShotsFromPayloadValue(),
      ...this.getShotMetadataFromPayload(),
    ].some((shot) => !!shot.compound);

    if ((this.isPreview || this.isPrinting) && !hasCompoundInShots) {
      return of([]);
    }

    const state = this.selectFilterOptionsMap['compound'];

    state.isLoading?.next(true);

    return this.widgetApiService.getCompounds({ Page: page, Rows: this.pageSize }).pipe(
      tap((compounds) => {
        const currentCompounds = state.items.value;
        const allCompounds =
          page === 1
            ? this.mergeCompounds(compounds, currentCompounds)
            : this.mergeCompounds(currentCompounds, compounds);

        state.items.next(allCompounds);
        state.isLoaded.next(true);

        this.compoundCurrentPage = page;
        this.hasMoreCompounds = compounds.length >= this.pageSize;
      }),
      catchError(() => {
        if (page === 1) {
          state.isLoaded.next(false);
        }

        this.hasMoreCompounds = false;

        return of([] as PneInjectable[]);
      }),
      finalize(() => {
        state.isLoading?.next(false);
      }),
    );
  }

  private resolveSelectedCompoundNames(): Observable<void> {
    this.syncSelectedCompoundNames();

    const hasUnresolvedCompound = this.shotsFormArray.controls.some((group) => {
      const id = this.normalizeString(group.get('compound')?.value);
      const name = this.normalizeString(group.get('compound_name')?.value);

      return !!id && !name;
    });

    if (!hasUnresolvedCompound || !this.hasMoreCompounds) {
      return of(undefined);
    }

    const state = this.selectFilterOptionsMap.compound;
    const compoundCount = state.items.value.length;
    const nextPage = this.compoundCurrentPage + 1;

    return this.loadCompoundData(nextPage).pipe(
      switchMap(() => {
        if (state.items.value.length === compoundCount) {
          return of(undefined);
        }

        return this.resolveSelectedCompoundNames();
      }),
    );
  }

  private syncSelectedCompoundNames(): void {
    this.shotsFormArray.controls.forEach((group) => {
      const compoundControl = group.get('compound');
      const nameControl = group.get('compound_name');

      if (!compoundControl?.value || this.normalizeString(nameControl?.value)) {
        return;
      }

      const name = this.resolveLabelByLookup('compound', compoundControl.value);

      if (name) {
        nameControl?.setValue(name, { emitEvent: false });
      }
    });
  }

  private addSelectedCompoundOptions(shots: ShotFormData[]): void {
    const selectedCompounds = shots.reduce<PneInjectable[]>((options, shot) => {
      const id = this.normalizeString(shot.compound);
      const name = this.normalizeString(shot.compound_name);

      if (id && name) {
        options.push({
          InjectableId: id,
          InjectableName: name,
        });
      }

      return options;
    }, []);

    const state = this.selectFilterOptionsMap.compound;

    state.items.next(this.mergeCompounds(state.items.value, selectedCompounds));
  }

  private addSelectedEmployeeOptions(): void {
    this.allEmployees = this.mergeEmployees(this.allEmployees, this.getSelectedEmployeeOptions());
    this.employees$.next(this.allEmployees);
  }

  private getSelectedEmployeeOptions(): { text: string; value: string }[] {
    return this.shotsFormArray.controls.reduce<{ text: string; value: string }[]>(
      (options, group) => {
        const value = this.normalizeString(group.get('employee_id')?.value);
        const savedName = this.normalizeString(group.get('employee_name')?.value);
        const loadedName = this.allEmployees.find(
          (employee) => String(employee.value) === value,
        )?.text;
        const text = savedName ?? loadedName;

        if (value && text) {
          options.push({ text, value });
        }

        return options;
      },
      [],
    );
  }

  private mergeEmployees(
    current: { text: string; value: string }[],
    incoming: { text: string; value: string }[],
  ): { text: string; value: string }[] {
    const seen = new Set(current.map((employee) => String(employee.value)));
    const uniqueIncoming = incoming.filter((employee) => {
      const id = String(employee.value);

      if (seen.has(id)) {
        return false;
      }

      seen.add(id);

      return true;
    });

    return [...current, ...uniqueIncoming];
  }

  private mergeCompounds(current: PneInjectable[], incoming: PneInjectable[]): PneInjectable[] {
    const seen = new Set(current.map((compound) => String(compound.InjectableId)));

    const uniqueIncoming = incoming.filter((compound) => {
      const id = String(compound.InjectableId);

      if (seen.has(id)) {
        return false;
      }

      seen.add(id);

      return true;
    });

    return [...current, ...uniqueIncoming];
  }

  private loadSitesData(): Observable<PneInjectionSite[]> {
    if (this.isPrinting || this.isEmrReadonly) {
      return of([]);
    }

    const hasSiteInShots = this.getShotMetadataFromPayload().some((shot) => !!shot.site);

    if ((this.isPreview || this.isPrinting) && !hasSiteInShots) {
      return of([]);
    }

    const state = this.selectFilterOptionsMap['site'];
    state.isLoading?.next(true);

    return this.widgetApiService.getSites().pipe(
      tap((sites) => {
        state.items.next(sites);
        state.isLoaded.next(true);

        if (sites && sites.length > 0) {
          const firstSiteId = sites[0].InjectionSiteId;

          this.shotsFormArray.controls.forEach((group) => {
            const siteControl = group.get('site');
            if (!siteControl?.value) {
              siteControl?.setValue(firstSiteId);
            }
          });
        }
      }),
      catchError(() => {
        state.isLoaded.next(false);
        return of([] as PneInjectionSite[]);
      }),
      finalize(() => {
        state.isLoading?.next(false);
      }),
    );
  }

  private loadFormData(): Observable<ShotFormData[]> {
    const payloadData = this.getShotsFromPayloadValue();
    if (payloadData.length) {
      const meaningfulPayloadRows = payloadData.filter((shot) => this.isShotDataMeaningful(shot));
      if (meaningfulPayloadRows.length) {
        return of(payloadData);
      }
    }

    const shotMetadata = this.getShotMetadataFromPayload();
    if (shotMetadata.length) {
      return of(shotMetadata);
    }

    if (this.isPreview || this.isPrinting || this.isEmrReadonly || !this.patient?.remote_id) {
      return of([]);
    }

    const params = {
      CustomerId: this.patient?.remote_id,
    };

    return this.widgetApiService.getCustomerShots(params).pipe(
      map((rows) =>
        rows.map((row): ShotFormData => {
          const record = row as unknown as Record<string, unknown>;
          const InjectableName = this.normalizeString(record['InjectableName']);
          const InjectionSiteName = this.normalizeString(record['InjectionSiteName']);
          const EmployeeName = this.normalizeString(record['EmployeeName']);

          return {
            id: row.Id,
            date: this.normalizeDate(row.InjectionDate),
            expire: this.normalizeDate(row.ExpirationDate),
            compound: this.normalizeString(row.InjectableId),
            compound_name: InjectableName,
            amount: row.Amount,
            uom: this.normalizeString(row.UOM),
            uom_name: null,
            site: this.normalizeString(row.InjectionSiteId),
            site_name: InjectionSiteName,
            employee_id: this.normalizeString(row.EmployeeId),
            employee_name: EmployeeName,
            lot_number: this.normalizeString(row.LotNumber),
            notes: this.normalizeString(row.InjectionNote),
          };
        }),
      ),
      catchError(() => of([])),
    );
  }

  private updatePayloadFromForm(): void {
    if (this.isInstanceDesign || this.suppressPayloadUpdate || this.isWidgetHidden) {
      return;
    }

    this.payload.properties.items.formInvalid = this.form.invalid;
    this.payload.properties.items.value = this.getValue();

    this.action.emit({
      component: FbEmrShotsComponent,
      data: { errorName: this.getErrorName() },
      type: 'validation-error',
    });
  }

  private getValue(): ShotFormData[] {
    return this.shotsFormArray.controls.map((group): ShotFormData => {
      const shot = group.getRawValue();
      const result: ShotFormData = {
        id: shot.id ?? null,
        date: this.normalizeDate(shot?.date),
        expire: this.normalizeDate(shot?.expire),
        compound: shot.compound ?? null,
        compound_name:
          this.resolveLabelByLookup('compound', shot.compound) ?? shot.compound_name ?? null,
        amount: shot.amount ?? null,
        uom: shot.uom ?? null,
        uom_name: this.resolveLabelByLookup('uom', shot.uom) ?? shot.uom_name ?? null,
        site: shot.site ?? null,
        site_name: this.resolveLabelByLookup('site', shot.site) ?? shot.site_name ?? null,
        employee_id: shot.employee_id ?? null,
        employee_name:
          this.resolveLabelByLookup('employee_id', shot.employee_id) ?? shot.employee_name ?? null,
        lot_number: shot.lot_number ?? null,
        notes: shot.notes ?? null,
      };

      copyScribeSuggestionMarker(
        this.markerTargetByGroup.get(group),
        result as unknown as ScribeSuggestionMarkerTarget,
      );
      this.markerTargetByGroup.set(group, result as unknown as ScribeSuggestionMarkerTarget);
      return result;
    });
  }

  private getErrorName(): string | null {
    const priority = ['required', 'matDatepickerParse'];
    const errors: string[] = [];

    const collectErrors = (control: AbstractControl): void => {
      if (control.errors) {
        errors.push(...Object.keys(control.errors));
      }

      if (control instanceof FormGroup) {
        Object.values(control.controls).forEach(collectErrors);
      } else if (control instanceof FormArray) {
        control.controls.forEach(collectErrors);
      }
    };

    collectErrors(this.form);

    return priority.find((e) => errors.includes(e)) ?? errors[0] ?? null;
  }

  private initializeFormFromPayload(value: ShotFormData[]): void {
    this.suppressPayloadUpdate = true;
    this.shotsFormArray.clear();

    if (isArray(value) && !isEmpty(value)) {
      value.forEach((shot) => {
        const group = this.createShotGroup(false, {
          id: shot?.id,
          date: this.normalizeDate(shot?.date),
          expire: this.normalizeDate(shot?.expire),
          compound: shot?.compound,
          compound_name: this.normalizeString(shot?.compound_name),
          amount: shot?.amount,
          uom: this.normalizeString(shot?.uom),
          uom_name: this.normalizeString(shot?.uom_name),
          site: this.normalizeString(shot?.site),
          site_name: this.normalizeString(shot?.site_name),
          employee_id: this.normalizeString(shot?.employee_id),
          employee_name: this.normalizeString(shot?.employee_name),
          lot_number: this.normalizeString(shot?.lot_number),
          notes: this.normalizeString(shot?.notes),
        });
        this.shotsFormArray.push(group);
        this.markerTargetByGroup.set(group, shot as ScribeSuggestionMarkerTarget);
      });
    } else {
      this.shotsFormArray.push(this.createShotGroup(false));
    }

    this.form.updateValueAndValidity({ emitEvent: false });
    this.suppressPayloadUpdate = false;
  }

  private applyDisabledState(): void {
    const editable = this.shouldInitializeDate && !this.isInstanceDesign;

    if (editable) {
      this.form.enable({ emitEvent: false });
    } else {
      this.form.disable({ emitEvent: false });
    }

    if (this.isEmrReadonly && this.payload?.properties?.items) {
      this.payload.properties.items.formInvalid = false;
    }

    this.cd.markForCheck();
  }

  private createShotGroup(isManuallyAdded: boolean, shot?: ShotFormData): ShotGroup {
    return this.fb.group({
      id: this.fb.control<string | null>(shot?.id),
      date: this.fb.control<string | null>(shot?.date, [Validators.required]),
      expire: this.fb.control<string | null>(shot?.expire),
      compound: this.fb.control<string | null>(shot?.compound, [Validators.required]),
      compound_name: this.fb.control<string | null>(shot?.compound_name ?? null),
      amount: this.fb.control<number | null>(shot?.amount, [
        Validators.required,
        Validators.min(0.001),
        Validators.max(10000),
      ]),
      uom: this.fb.control<string | null>(shot?.uom ?? this.getDefaultUomValue(), [
        Validators.required,
      ]),
      uom_name: this.fb.control<string | null>(shot?.uom_name ?? null),
      site: this.fb.control<string | null>(shot?.site ?? this.getDefaultSiteValue(), [
        Validators.required,
      ]),
      site_name: this.fb.control<string | null>(shot?.site_name ?? null),
      employee_id: this.fb.control<string | null>(shot?.employee_id),
      employee_name: this.fb.control<string | null>(shot?.employee_name ?? null),
      lot_number: this.fb.control<string | null>(shot?.lot_number),
      notes: this.fb.control<string | null>(shot?.notes),
      isManuallyAdded: this.fb.control<boolean>(isManuallyAdded),
    });
  }

  private getDefaultUomValue(): string | null {
    if (this.isPreview || this.isPrinting || !this.patient) {
      return null;
    }

    return this.selectFilterOptionsMap?.uom?.items?.value[0]?.UomId ?? null;
  }

  private getDefaultSiteValue(): string | null {
    if ((this.isPreview || this.isPrinting) && !this.formTemplateId && !this.patient) {
      return null;
    }

    return this.selectFilterOptionsMap?.site?.items.value?.[0]?.InjectionSiteId ?? null;
  }

  private normalizeDate(value: string | Date): string | null {
    if (!value) {
      return null;
    }

    const parsed = parseLocalDateString(value);
    if (!parsed?.trim().length) {
      return null;
    }

    // Backend sometimes sends "1/1/0001"; treat it as invalid.
    if (parsed.startsWith('0001-')) {
      return null;
    }

    return parsed;
  }

  private normalizeString(value: unknown): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    const str = String(value).trim();
    return str.length ? str : null;
  }

  private normalizeComparableString(value: unknown): string | null {
    const normalized = this.normalizeString(value);
    return normalized ? normalized.toLowerCase() : null;
  }

  private resolveReviewSelection(
    fieldName: 'compound' | 'site' | 'uom' | 'employee_id',
    value: unknown,
  ): { id: string | null; label: string | null } {
    const normalized = this.normalizeString(value);
    const comparable = this.normalizeComparableString(value);
    if (!normalized) {
      return { id: null, label: null };
    }

    if (fieldName === 'compound') {
      const option = this.selectFilterOptionsMap.compound.items.value.find(
        (item) =>
          String(item.InjectableId) === normalized ||
          this.normalizeComparableString(item.InjectableName) === comparable,
      );
      return {
        id: this.normalizeString(option?.InjectableId ?? normalized),
        label: this.normalizeString(option?.InjectableName ?? normalized),
      };
    }

    if (fieldName === 'site') {
      const option = this.selectFilterOptionsMap.site.items.value.find(
        (item) =>
          String(item.InjectionSiteId) === normalized ||
          this.normalizeComparableString(item.InjectionSiteName) === comparable,
      );
      return {
        id: this.normalizeString(option?.InjectionSiteId ?? normalized),
        label: this.normalizeString(option?.InjectionSiteName ?? normalized),
      };
    }

    if (fieldName === 'uom') {
      const option = this.selectFilterOptionsMap.uom.items.value.find(
        (item) =>
          String(item.UomId) === normalized ||
          this.normalizeComparableString(item.UomName) === comparable,
      );
      return {
        id: this.normalizeString(option?.UomId ?? normalized),
        label: this.normalizeString(option?.UomName ?? normalized),
      };
    }

    const option = this.employees$.value.find(
      (item) =>
        String(item.value) === normalized ||
        this.normalizeComparableString(item.text) === comparable,
    );
    return {
      id: this.normalizeString(option?.value ?? normalized),
      label: this.normalizeString(option?.text ?? normalized),
    };
  }

  private resolveLabelByLookup(fieldName: string, value: string | null | undefined): string | null {
    if (!value) {
      return null;
    }

    const normalizedValue = String(value);
    if (fieldName === 'compound') {
      const option = this.selectFilterOptionsMap.compound.items.value.find(
        (item) => String(item.InjectableId) === normalizedValue,
      );
      return this.normalizeString(option?.InjectableName);
    }

    if (fieldName === 'site') {
      const option = this.selectFilterOptionsMap.site.items.value.find(
        (item) => String(item.InjectionSiteId) === normalizedValue,
      );
      return this.normalizeString(option?.InjectionSiteName);
    }

    if (fieldName === 'uom') {
      const option = this.selectFilterOptionsMap.uom.items.value.find(
        (item) => String(item.UomId) === normalizedValue,
      );
      return this.normalizeString(option?.UomName);
    }

    if (fieldName === 'employee_id') {
      const option = this.employees$.value.find((item) => String(item.value) === normalizedValue);
      return this.normalizeString(option?.text);
    }

    return null;
  }

  private isShotMeaningful(group: AbstractControl): boolean {
    const value = group.getRawValue() as Record<string, unknown>;
    return this.hasMeaningfulShotContent(
      (key) => value[key],
      this.getDefaultUomValue(),
      this.getDefaultSiteValue(),
    );
  }

  private isShotDataMeaningful(shot: ShotFormData): boolean {
    return this.hasMeaningfulShotContent(
      (key) => shot?.[key as keyof ShotFormData],
      this.getDefaultUomValue(),
      this.getDefaultSiteValue(),
    );
  }

  private hasMeaningfulShotContent(
    getValue: (key: string) => unknown,
    defaultUom: string | null,
    defaultSite: string | null,
  ): boolean {
    const keys = [
      'date',
      'expire',
      'compound',
      'amount',
      'uom',
      'site',
      'employee_id',
      'lot_number',
      'notes',
    ];

    return keys.some((key) => {
      const item = getValue(key);
      if (item === null || item === undefined) {
        return false;
      }

      if (key === 'uom' && defaultUom !== null && String(item).trim() === defaultUom) {
        return false;
      }

      if (key === 'site' && defaultSite !== null && String(item).trim() === defaultSite) {
        return false;
      }

      if (typeof item === 'number') {
        return Number.isFinite(item);
      }

      return String(item).trim() !== '';
    });
  }

  public hideWidget(): void {
    if (this.isInstanceDesign || this.isWidgetHidden) {
      return;
    }

    this.isWidgetHidden = true;
    this.suppressPayloadUpdate = true;
    this.shotsFormArray.clear();
    this.form.updateValueAndValidity({ emitEvent: false });

    if (this.payload?.properties?.items) {
      this.payload.properties.items.value = null;
      this.payload.properties.items.formInvalid = false;
      this.payload.properties.items.hidden = true;
    }

    this.suppressPayloadUpdate = false;
    this.cd.markForCheck();
  }

  private showWidget(): void {
    if (this.isInstanceDesign || !this.isWidgetHidden) {
      return;
    }

    this.isWidgetHidden = false;

    if (this.payload?.properties?.items) {
      this.payload.properties.items.hidden = false;
      if (!Array.isArray(this.payload.properties.items.value)) {
        this.payload.properties.items.value = [];
      }
    }
  }
}
